import { AfterViewInit, Directive, ElementRef, inject, OnDestroy, output } from '@angular/core';

/**
 * Emits whether a line-clamped element is actually overflowing its visible box,
 * so a "Read more" affordance can be shown only when text is genuinely cut off (#48894).
 *
 * Uses a ResizeObserver rather than a single measurement: inside the notification popover the
 * first measurement can run before the overlay has finished laying out, which would report the
 * text as "not overflowing" and hide the toggle until the list next re-rendered. The observer
 * fires once when observation starts (after layout settles) and again on any later resize, so
 * the toggle appears reliably on first open.
 */
@Directive({
  selector: '[appOverflowClamp]',
  standalone: true,
})
export class OverflowClampDirective implements AfterViewInit, OnDestroy {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly overflowChange = output<boolean>();

  private observer?: ResizeObserver;

  ngAfterViewInit(): void {
    this.measure();
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure());
      this.observer.observe(this.host.nativeElement);
    }
  }

  private measure(): void {
    const el = this.host.nativeElement;
    // +1 absorbs sub-pixel rounding so a perfectly-fitting line never reads as overflowing.
    this.overflowChange.emit(el.scrollHeight > el.clientHeight + 1);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
